import { useEffect, useMemo, useState } from 'react';
import DataToolbar from './components/DataToolbar.jsx';
import GemListPage from './pages/GemListPage.jsx';
import GemReviewPage from './pages/GemReviewPage.jsx';
import RecommendPage from './pages/RecommendPage.jsx';
import EquippedPage from './pages/EquippedPage.jsx';
import StatusPage from './pages/StatusPage.jsx';
import { createTranslator } from './lib/i18n.js';
import { loadAstroguide } from './lib/astro.js';
import { downloadJson, saveJsonToHandle, tryDeleteImage } from './lib/fileIo.js';
import { normalizeLoadedGems, reindexGems } from './lib/gemLogic.js';

const TABS = [
  ['list', '📋'], ['review', '✏️'], ['recommend', '⚔️'], ['equipped', '🔎'], ['pending', '📦'], ['trash', '🗑️'],
];

export default function App() {
  const [lang, setLangState] = useState(() => localStorage.getItem('msl-lang') || 'ko');
  const [tab, setTab] = useState('list');
  const [gems, setGems] = useState([]);
  const [astro, setAstro] = useState([]);
  const [astroError, setAstroError] = useState('');
  const [sourceHandle, setSourceHandle] = useState(null);
  const [fileName, setFileName] = useState('');
  const [imageSource, setImageSource] = useState({ directoryHandle: null, fileMap: null, label: '' });
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);
  const t = createTranslator(lang);

  useEffect(() => {
    loadAstroguide().then(setAstro).catch((error) => setAstroError(error.message));
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem('msl-lang', lang);
  }, [lang]);

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  const counts = useMemo(() => ({
    normal: gems.filter((g) => !['보류', '휴지통'].includes(g.status)).length,
    pending: gems.filter((g) => g.status === '보류').length,
    trash: gems.filter((g) => g.status === '휴지통').length,
    uncertain: gems.filter((g) => !['보류', '휴지통'].includes(g.status) && g.is_uncertain).length,
  }), [gems]);

  const checked = gems.length ? gems.filter((g) => !g.is_uncertain).length : 0;
  const progress = gems.length ? Math.round(checked / gems.length * 100) : 0;

  function setLang(value) { setLangState(value); }

  async function loadJson(data, source) {
    if (dirty && gems.length && !window.confirm(t('discardChanges'))) return;
    const normalized = normalizeLoadedGems(data);
    const ids = new Set(normalized.map((g) => g.id));
    const finalData = ids.size === normalized.length ? normalized : reindexGems(normalized);
    setGems(finalData);
    setSourceHandle(source?.handle || null);
    setFileName(source?.fileName || 'parsed_gems.json');
    setDirty(false);
    setTab('list');
    setMessage({ type: 'success', text: `${finalData.length.toLocaleString()}개 보석을 불러왔습니다.` });
  }

  function changeGems(next) {
    setGems(next);
    setDirty(true);
  }

  async function saveSource() {
    try {
      const finalData = reindexGems(gems);
      await saveJsonToHandle(sourceHandle, finalData);
      setGems(finalData);
      setDirty(false);
      setMessage({ type: 'success', text: '원본 JSON 파일에 저장했습니다.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  }

  function exportJson() {
    const finalData = reindexGems(gems);
    downloadJson(finalData, fileName || 'parsed_gems.json');
    setMessage({ type: 'success', text: '수정된 JSON을 다운로드했습니다.' });
  }

  async function permanentDelete(ids) {
    const selected = new Set(ids);
    let deletedImages = 0;
    if (imageSource.directoryHandle) {
      for (const g of gems) {
        if (selected.has(g.id) && g.status === '휴지통' && g.file_name) {
          if (await tryDeleteImage(imageSource.directoryHandle, g.file_name)) deletedImages += 1;
        }
      }
    }
    const next = reindexGems(gems.filter((g) => !(selected.has(g.id) && g.status === '휴지통')));
    setGems(next);
    setDirty(true);
    setMessage({
      type: 'success',
      text: imageSource.directoryHandle
        ? `보석 ${ids.length}개 삭제 · 이미지 ${deletedImages}개 삭제`
        : `보석 ${ids.length}개를 데이터에서 삭제했습니다. 이미지 폴더 쓰기 권한이 없어 원본 이미지는 삭제하지 않았습니다.`,
    });
  }

  return (
    <div className="app-shell">
      <header className="top-header">
        <div>
          <h1>💎 {t('appTitle')}</h1>
          <p>{t('appSubtitle')}</p>
        </div>
        <div className="privacy-pill">🔒 {t('localOnly')}</div>
      </header>

      <DataToolbar
        t={t}
        lang={lang}
        setLang={setLang}
        onLoadJson={loadJson}
        onSaveSource={saveSource}
        onExport={exportJson}
        onSetImageSource={setImageSource}
        fileName={fileName}
        hasSourceHandle={Boolean(sourceHandle)}
        gemCount={gems.length}
        dirty={dirty}
        setMessage={setMessage}
      />

      {message && <div className={`toast ${message.type || 'info'}`}>{message.text}</div>}
      {astroError && <div className="toast error">Astroguide: {astroError}</div>}

      {!gems.length ? (
        <main className="welcome">
          <div className="welcome-card">
            <div className="welcome-icon">💎</div>
            <h2>{t('noData')}</h2>
            <p>현재 웹사이트에는 개인 보석 JSON과 캡처 이미지를 포함하지 않습니다. 상단의 <b>{t('loadJson')}</b>을 눌러 PC의 파일을 선택하세요.</p>
            <div className="welcome-grid">
              <div><b>1</b><span>Python 캡처 → gem_images</span></div>
              <div><b>2</b><span>Gemini 분석 → parsed_gems.json</span></div>
              <div><b>3</b><span>웹에서 JSON + 이미지 폴더 연결</span></div>
              <div><b>4</b><span>검수 · 추천 · 장착 후 JSON 저장</span></div>
            </div>
            <p className="muted">Astroguide: {astro.length ? `${astro.length.toLocaleString()} monsters loaded` : 'loading...'}</p>
          </div>
        </main>
      ) : (
        <>
          <nav className="tabbar">
            {TABS.map(([key, icon]) => {
              const count = key === 'pending' ? counts.pending : key === 'trash' ? counts.trash : key === 'review' ? counts.uncertain : null;
              return <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{icon} {t(key)} {count != null && <span className="count-badge">{count}</span>}</button>;
            })}
          </nav>

          <main className="main-content">
            {tab === 'list' && <GemListPage gems={gems} onChange={changeGems} t={t} />}
            {tab === 'review' && <GemReviewPage gems={gems} onChange={changeGems} imageSource={imageSource} t={t} />}
            {tab === 'recommend' && <RecommendPage gems={gems} astro={astro} onChange={changeGems} imageSource={imageSource} lang={lang} t={t} setMessage={setMessage} />}
            {tab === 'equipped' && <EquippedPage gems={gems} astro={astro} onChange={changeGems} imageSource={imageSource} lang={lang} t={t} />}
            {tab === 'pending' && <StatusPage mode="보류" gems={gems} onChange={changeGems} onPermanentDelete={permanentDelete} t={t} />}
            {tab === 'trash' && <StatusPage mode="휴지통" gems={gems} onChange={changeGems} onPermanentDelete={permanentDelete} t={t} />}
          </main>

          <footer className="progress-footer">
            <div><span>검수 완료율</span><b>{progress}%</b><small>{checked.toLocaleString()} / {gems.length.toLocaleString()}</small></div>
            <progress value={progress} max="100" />
            {imageSource.label && <span className="muted">🖼️ {imageSource.label}</span>}
          </footer>
        </>
      )}
    </div>
  );
}
